import React, { useState, useCallback, useRef, useEffect } from "react";
import * as R from "ramda";
import { useRoom } from "../contexts/rooms";
import Spinner from "../components/Spinner";
import Navbar from "../components/Navbar";
import Page from "../components/Page";
import MapInteraction from "../lib/react-map-interaction/MapInteraction";
import bgBlank from "../images/bg-blank.png";
import reloadPage from "../lib/reloadAndUpdate";
import navigationMessages from "../i18n/navigation";
import "./RoomResult.css";
import Header from "../components/Header";

const TILE_SIZE = 355;

const calcTranslationBounds = (viewport, data, wrapperSize) => {
  if (!wrapperSize) {
    return;
  }
  const w = wrapperSize.width;
  const h = wrapperSize.height;
  const height = +data.height;
  const width = +data.width;
  const imageWidth = width * TILE_SIZE;
  const imageHeight = height * TILE_SIZE;

  const imageWidthScaled = imageWidth * viewport.scale;
  const imageHeightScaled = imageHeight * viewport.scale;

  const xMin = imageWidthScaled <= w ? 0 : -(imageWidthScaled - w);
  const xMax = 0;

  const yMin = imageHeightScaled <= h ? 0 : -(imageHeightScaled - h);
  const yMax = 0;

  const translationBounds = {
    xMin,
    xMax,
    yMin,
    yMax,
  };
  return translationBounds;
};

export function RoomResultView({
  match: {
    params: { roomId },
  },
}) {
  const wrapperRef = useRef();
  const mapContainerRef = useRef();
  const canvasContainerRef = useRef();
  const stateRef = useRef({ width: null, height: null });
  const { data, loading, error } = useRoom(roomId);
  const [viewport, setViewport] = useState({
    scale: 1,
    translation: { x: 0, y: 0 },
    translationBounds: { xMin: 0, yMin: 0, xMax: 355, yMax: 355 },
  });
  const [wrapperSize, setWrapperSize] = useState();
  const [scaleBoundaries, setScaleBoundaries] = useState({
    minScale: 1,
    maxScale: 1,
  });
  useEffect(() => {
    if (wrapperRef.current && !wrapperSize) {
      const width = wrapperRef.current.clientWidth;
      const height = wrapperRef.current.clientHeight;
      setWrapperSize({ width, height });
    }
  });
  useEffect(() => {
    const onResize = () => {
      if (wrapperRef.current && mapContainerRef.current) {
        const oldDisplay = mapContainerRef.current.style.display;
        mapContainerRef.current.style.display = "none";
        const width = wrapperRef.current.clientWidth;
        const height = wrapperRef.current.clientHeight;
        mapContainerRef.current.style.display = oldDisplay;
        setWrapperSize({ width, height });
      }
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  });
  const onChangeViewport = useCallback(
    (newViewport) => {
      const translationBounds = calcTranslationBounds(
        newViewport,
        data,
        wrapperSize
      );

      const finalViewport = {
        ...viewport,
        translationBounds,
        scale: newViewport.scale,
        translation: newViewport.translation,
      };

      setViewport(finalViewport);
    },
    [setViewport, data, wrapperSize, viewport]
  );
  useEffect(() => {
    if (
      data &&
      wrapperSize &&
      (stateRef.current.width !== wrapperSize.width ||
        stateRef.current.height !== wrapperSize.height)
    ) {
      stateRef.current.width = wrapperSize.width;
      stateRef.current.height = wrapperSize.height;
      const height = +data.height;
      const width = +data.width;
      const imageWidth = width * TILE_SIZE;
      const imageHeight = height * TILE_SIZE;
      const w = wrapperSize.width;
      const h = wrapperSize.height;
      const minScale = Math.min(h / imageHeight, w / imageWidth);
      setScaleBoundaries({
        minScale,
        maxScale: 3,
      });

      const translationBounds = calcTranslationBounds(
        viewport,
        data,
        wrapperSize
      );

      const result = {
        ...viewport,
        scale: minScale,
        translation: { x: 0, y: 0 },
        translationBounds,
      };
      setViewport(result);
    }
  }, [data, viewport, wrapperSize]);
  if (loading) {
    return <Spinner size="l" />;
  }
  if (error) {
    return <pre>{JSON.stringify(error, null, 2)}</pre>;
  }
  if (data) {
    return (
      <Page className="Page__fullwidth">
        <Navbar
          items={[
            { to: `/rooms/${roomId}`, text: navigationMessages.room },
            { to: "/", text: navigationMessages.reload, onClick: reloadPage },
          ]}
        />
        <Header
          size="h1"
          paddingX={10}
          style={{
            width: "100%",
            margin: "0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingBottom: 10,
          }}
        >
          {data.name}{" "}
          <i style={{ fontSize: "0.5em" }}>
            {(viewport.scale * 100).toFixed(0)}%
          </i>
        </Header>
        <div
          style={{ display: "flex", width: "100%", flex: "1 0 auto" }}
          ref={wrapperRef}
        >
          {wrapperSize && (
            <div style={wrapperSize} ref={mapContainerRef}>
              <MapInteraction
                disablePan={false}
                disableZoom={false}
                scale={viewport.scale}
                defaultTranslation={{ x: 0, y: 0 }}
                translationBounds={viewport.translationBounds}
                translation={viewport.translation}
                defaultScale={1}
                minScale={scaleBoundaries.minScale}
                maxScale={scaleBoundaries.maxScale}
                onChange={onChangeViewport}
              >
                {(viewport) => (
                  <div
                    className="RoomResult--canvas-container"
                    ref={canvasContainerRef}
                  >
                    <TotalCanvas room={data} viewport={viewport} />
                  </div>
                )}
              </MapInteraction>
            </div>
          )}
        </div>
      </Page>
    );
  }
  return (
    <Page>
      <Navbar
        items={[
          { to: `/rooms}`, text: "📚 Rooms" },
          { to: `/logout}`, text: "🚪Logout" },
        ]}
      />
      <Header size="h3" paddingX={10}>
        Room {roomId} not found
      </Header>
    </Page>
  );
}

function TotalCanvas({
  room: { tiles = {}, width, height },
  viewport: {
    scale,
    translation: { x, y },
  },
}) {
  const tilesPairs = R.toPairs(tiles);
  return (
    <div
      className="TotalCanvas"
      style={{
        backgroundImage: `url(${bgBlank})`,
        width: width * TILE_SIZE * scale,
        height: height * TILE_SIZE * scale,
        left: x,
        top: y,
      }}
    >
      {tilesPairs.map(([id, tile]) => {
        const style = {
          width: TILE_SIZE * scale,
          height: TILE_SIZE * scale,
          top: tile.y * TILE_SIZE * scale,
          left: tile.x * TILE_SIZE * scale,
        };
        return tile.src ? (
          <img
            key={id}
            className="TotalCanvas--img"
            style={style}
            src={tile.src}
            alt="tile"
          />
        ) : null;
      })}
    </div>
  );
}

export default RoomResultView;
